import videoThumbnail from "@/assets/announcements/prospecting-in-chat-video.webp";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "@/hooks/useTranslation";
import {
  hasSeenProspectingAnnouncement,
  markProspectingAnnouncementSeen,
  PROSPECTING_IN_CHAT_VIDEO_URL,
} from "@/lib/prospecting-announcement";
import {
  openSupportConversation,
  openSupportEmail,
  useSupportMessengerReady,
} from "@/lib/support";
import { useUser } from "@clerk/react";
import { MessageSquareText, PlayIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * The "prospecting moves into the chat" announcement on the desktop chat: an
 * Intercom-post-style modal that opens on its own the first time only, and a
 * banner that stays above the page to reopen it until the user closes it. The
 * caller gates it on `useProspectingAnnouncement`.
 */
export function ProspectingChatAnnouncement({
  isBannerShown,
  onBannerDismiss,
}: {
  readonly isBannerShown: boolean;
  readonly onBannerDismiss: () => void;
}) {
  const { user, isLoaded } = useUser();
  const userId = user?.id;
  const [isOpen, setIsOpen] = useState(false);

  // Marked seen as soon as it opens: however the user leaves it — close
  // button, Escape, overlay click, or a CTA — it never comes back on its own.
  useEffect(() => {
    if (!isLoaded || hasSeenProspectingAnnouncement(userId)) return;
    markProspectingAnnouncementSeen(userId);
    setIsOpen(true);
  }, [isLoaded, userId]);

  return (
    <>
      {isBannerShown && (
        <ProspectingChatAnnouncementBanner
          onOpen={() => setIsOpen(true)}
          onDismiss={onBannerDismiss}
        />
      )}
      <ProspectingChatAnnouncementDialog
        open={isOpen}
        onOpenChange={setIsOpen}
      />
    </>
  );
}

function ProspectingChatAnnouncementBanner({
  onOpen,
  onDismiss,
}: {
  readonly onOpen: () => void;
  readonly onDismiss: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="relative flex items-center justify-center gap-4 h-10 px-12 text-sm text-white bg-gradient-2">
      <span className="truncate">
        <span className="font-semibold">
          {t("prospecting.chatAnnouncement.banner.title")}
        </span>{" "}
        <span className="hidden text-white/85 xl:inline">
          {t("prospecting.chatAnnouncement.banner.description")}
        </span>
      </span>
      <Button
        size="xs"
        variant="secondary"
        className="shrink-0 rounded-full bg-white text-[#0b0b1f] hover:bg-white/90"
        onClick={onOpen}
      >
        <PlayIcon className="fill-current" />
        {t("prospecting.chatAnnouncement.banner.action")}
      </Button>
      <Button
        size="icon-xs"
        variant="ghost"
        className="absolute right-3 text-white/80 hover:bg-white/15 hover:text-white"
        aria-label={t("prospecting.chatAnnouncement.banner.dismiss")}
        onClick={onDismiss}
      >
        <XIcon />
      </Button>
    </div>
  );
}

const FEATURES = [
  "words",
  "engine",
  "lookalikes",
  "files",
  "report",
  "portfolio",
] as const;

function ProspectingChatAnnouncementDialog({
  open,
  onOpenChange,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMessengerReady = useSupportMessengerReady();

  const goToChat = () => {
    onOpenChange(false);
    navigate("/chat");
  };

  // The messenger can only pre-fill the composer — Intercom never sends on the
  // user's behalf — so the message is ready and one click away. Without a
  // booted messenger, the mailbox takes over.
  const messageUs = (message: string) => {
    onOpenChange(false);
    if (isMessengerReady) {
      openSupportConversation(message);
    } else {
      openSupportEmail();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100%-2rem)] max-w-[640px] sm:max-w-[640px] max-h-[90dvh] gap-0 p-0 overflow-y-auto"
        // An announcement, not a form: no focus ring parked on the first CTA
        // as it opens. Focus stays trapped inside all the same.
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="flex flex-col gap-5 px-6 sm:px-10 py-8 text-[15px] leading-relaxed">
          <DialogTitle className="text-xl font-bold leading-snug">
            {t("prospecting.chatAnnouncement.title")}
          </DialogTitle>

          <div className="flex flex-col items-center gap-3">
            <a
              href={PROSPECTING_IN_CHAT_VIDEO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="relative block w-full overflow-hidden rounded-lg group"
              aria-label={t("prospecting.chatAnnouncement.video")}
            >
              <img
                src={videoThumbnail}
                alt=""
                className="block w-full h-auto transition-transform duration-300 group-hover:scale-[1.02]"
              />
              <span className="absolute inset-0 flex items-center justify-center bg-black/10 transition-colors group-hover:bg-black/25">
                <span className="flex items-center justify-center rounded-full size-14 bg-white/90 shadow-lg">
                  <PlayIcon className="size-6 translate-x-0.5 fill-[#0b0b1f] text-[#0b0b1f]" />
                </span>
              </span>
            </a>
            <a
              href={PROSPECTING_IN_CHAT_VIDEO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-majorelle underline underline-offset-2 hover:text-majorelle/80"
            >
              <PlayIcon className="size-4 fill-current" />
              {t("prospecting.chatAnnouncement.video")}
            </a>
          </div>

          <DialogDescription className="text-[15px] leading-relaxed text-foreground">
            {t("prospecting.chatAnnouncement.intro")}
          </DialogDescription>

          {FEATURES.map((feature) => (
            <p key={feature}>
              <span aria-hidden="true">
                {t(`prospecting.chatAnnouncement.features.${feature}.emoji`)}
              </span>{" "}
              <span className="font-semibold">
                {t(`prospecting.chatAnnouncement.features.${feature}.title`)}
              </span>{" "}
              —{" "}
              {t(
                `prospecting.chatAnnouncement.features.${feature}.description`,
              )}
            </p>
          ))}

          <p>{t("prospecting.chatAnnouncement.videoTeaser")}</p>

          <p>
            <span aria-hidden="true">🎙️</span>{" "}
            <span className="font-semibold">
              {t("prospecting.chatAnnouncement.webinar.title")}
            </span>{" "}
            {t("prospecting.chatAnnouncement.webinar.description")}{" "}
            <span aria-hidden="true">👉</span>{" "}
            <Button
              variant="link"
              className="h-auto p-0 text-[15px] text-majorelle underline underline-offset-2"
              onClick={() =>
                messageUs(t("prospecting.chatAnnouncement.webinar.message"))
              }
            >
              {t("prospecting.chatAnnouncement.webinar.action")}
            </Button>
          </p>

          <div className="flex flex-col items-center gap-2 pt-2">
            <Button size="lg" className="px-10" onClick={goToChat}>
              {t("prospecting.chatAnnouncement.tryInChat")}
            </Button>
            <Button
              variant="link"
              onClick={() =>
                messageUs(t("prospecting.chatAnnouncement.contact.message"))
              }
            >
              <MessageSquareText />
              {t("prospecting.chatAnnouncement.contact.action")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
